import {
  TraceKeyResponseTypeEnum,
  type TraceKey,
} from '@ulysses-inc/harami_api_client'
import { useCallback, useMemo, useState } from 'react'
import { useQueryTraceKeys } from 'src/features/traceKeys/api/useQueryTraceKeys'
import { UnionTraceKeyCondition } from '../../types'

export type TraceKeyConditionFilter = {
  traceKeyName: TraceKey['name']
} & UnionTraceKeyCondition

export const useTraceKeyFilter = (
  initialTraceKeyConditions: UnionTraceKeyCondition[],
) => {
  const { data: traceKeys = [], isLoading } = useQueryTraceKeys()

  const [selectedConditions, setSelectedConditions] = useState<
    UnionTraceKeyCondition[]
  >(initialTraceKeyConditions)

  const traceKeyConditionFilters = useMemo<TraceKeyConditionFilter[]>(() => {
    // ロード完了後は存在する検索キーのみに絞り込む（削除された検索キーを除外）
    const validConditions = isLoading
      ? selectedConditions
      : selectedConditions.filter(condition =>
          traceKeys.some(tk => tk.id === condition.traceKeyId),
        )
    return validConditions.map(condition => ({
      ...condition,
      traceKeyName:
        traceKeys.find(tk => tk.id === condition.traceKeyId)?.name || '',
    }))
  }, [selectedConditions, traceKeys, isLoading])

  const availableTraceKeys = useMemo(() => {
    const usedTraceKeyIds = new Set(
      traceKeyConditionFilters.map(t => t.traceKeyId),
    )
    return traceKeys.filter(traceKey => !usedTraceKeyIds.has(traceKey.id))
  }, [traceKeys, traceKeyConditionFilters])

  const createResponseSetCondition = (
    traceKey: TraceKey,
  ): UnionTraceKeyCondition => ({
    traceKeyId: traceKey.id,
    responseType: TraceKeyResponseTypeEnum.ResponseSet,
    responseSet: { value: '' },
  })

  const createDateCondition = (traceKey: TraceKey): UnionTraceKeyCondition => ({
    traceKeyId: traceKey.id,
    responseType: TraceKeyResponseTypeEnum.Date,
    datetime: null,
  })

  const createTextCondition = (traceKey: TraceKey): UnionTraceKeyCondition => ({
    traceKeyId: traceKey.id,
    responseType: TraceKeyResponseTypeEnum.Text,
    text: { value: '' },
  })

  const conditionFactories = {
    [TraceKeyResponseTypeEnum.ResponseSet]: createResponseSetCondition,
    [TraceKeyResponseTypeEnum.Date]: createDateCondition,
    [TraceKeyResponseTypeEnum.Text]: createTextCondition,
  } as const

  const createTraceKeyCondition = (
    traceKey: TraceKey,
  ): UnionTraceKeyCondition => {
    const factory = conditionFactories[traceKey.responseType]
    if (!factory) {
      throw new Error(`Unsupported response type: ${traceKey.responseType}`)
    }
    return factory(traceKey)
  }

  const addTraceKeyConditionFilter = (traceKey: TraceKey) => {
    setSelectedConditions(prev => {
      return [...prev, createTraceKeyCondition(traceKey)]
    })
  }

  const removeTraceKeyConditionFilter = useCallback(
    (input: TraceKeyConditionFilter) => {
      setSelectedConditions(prev =>
        prev.filter(item => item.traceKeyId !== input.traceKeyId),
      )
    },
    [],
  )

  const updateTraceKeyConditionFilter = useCallback(
    (newFilter: TraceKeyConditionFilter) => {
      const { traceKeyName, ...condition } = newFilter
      setSelectedConditions(prev =>
        prev.map(item =>
          item.traceKeyId === condition.traceKeyId ? condition : item,
        ),
      )
    },
    [],
  )

  const clearTraceKeyConditionFilters = useCallback(() => {
    setSelectedConditions([])
  }, [])

  const hasDateTraceKeyFilter = useMemo(() => {
    return traceKeyConditionFilters.some(
      filter =>
        filter.responseType === TraceKeyResponseTypeEnum.Date &&
        filter.datetime?.greaterThanOrEqual &&
        filter.datetime?.lessThan,
    )
  }, [traceKeyConditionFilters])

  return {
    availableTraceKeys,
    addTraceKeyConditionFilter,
    removeTraceKeyConditionFilter,
    updateTraceKeyConditionFilter,
    clearTraceKeyConditionFilters,
    isLoading,
    traceKeyConditionFilters,
    hasDateTraceKeyFilter,
  } as const
}
